import { yupResolver } from '@hookform/resolvers/yup'
import { SelectIdGroupContext } from '_context/SelectGroupContext'
import { getListEventsOpeningByHost } from '_redux/modules/event'
import { DEFAULT_PRODUCT, DEFAULT_SUB_ITEM } from '_utils/constant'
import { validationSchema } from '_utils/validation'
import { useCallback, useContext, useEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useHistory, useLocation } from 'react-router'

import { addProductToEventAction, editProductAction } from '_redux/modules/product'
import { useDispatch, useSelector } from 'react-redux'
import Select from 'react-select'
import { useToasts } from 'react-toast-notifications'
import { SelectProductContext } from '../../context/ProductContext'
import { SPLIT_CASE_CONDITION_FULL } from '../../utils/constant'
import DesktopProductList from './components/DesktopProductList'
import './style.scss'

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    width: '400px',
    borderColor: state.isFocused ? '#179d9a' : '#ced4da',
    boxShadow: state.isFocused ? ' #ecfbfb' : 'none',
    fontSize: '16px',
    '&:hover': {
      borderColor: state.isFocused ? '#179d9a' : '#ced4da',
    },
  }),
  menu: (provided) => ({
    ...provided,
    width: '400px',
    fontSize: '16px',
  }),
  option: (provided, state) => ({
    ...provided,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    backgroundColor: state.isSelected ? '#ecfbfb' : state.isFocused ? '#f4fdfd' : 'white',
    color: '#212529',
    cursor: 'pointer',
  }),
  singleValue: (provided) => ({
    ...provided,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }),
}

const formatProductData = (product) => {
  let { splitCasePurchase } = product

  if (
    !splitCasePurchase ||
    !splitCasePurchase.costPerItem ||
    !splitCasePurchase.itemsPerCase ||
    !splitCasePurchase.name
  ) {
    splitCasePurchase = {}
    product.pSplitCasePurchase = false
  } else {
    if (
      (!splitCasePurchase.photoUrl || splitCasePurchase.photoUrl === '') &&
      product.photoUrls &&
      product.photoUrls.length > 0
    ) {
      splitCasePurchase.photoUrl = product.photoUrls[0]
    }
    splitCasePurchase = {
      enabled: true,
      ...splitCasePurchase,
      itemsPerCase: Number(splitCasePurchase.itemsPerCase),
      costPerItem: Number(splitCasePurchase.costPerItem),
      minItemOrder: Number(splitCasePurchase.minItemOrder || 1),
      isFullCase: splitCasePurchase.fulfillCondition === SPLIT_CASE_CONDITION_FULL,
      acknowledgementRequiredPayAfter: !!splitCasePurchase.acknowledgementRequiredPayAfter,
    }
    product.pSplitCasePurchase = true
  }

  const {
    name,
    description,
    price,
    limitPerOrder,
    maxQuantity,
    defaultQuantity,
    minOrderQty,
    photoUrls,
    subItems,
  } = product

  const formatted = {
    name,
    description: description || '',
    price: Number(price),
    limitPerOrder: Number(limitPerOrder),
    maxQuantity: Number(maxQuantity),
    defaultQuantity: Number(defaultQuantity || 0),
    minOrderQty: Number(minOrderQty || 1),
    photoUrls: Array.isArray(photoUrls) ? photoUrls : [],
    pSplitCasePurchase: product.pSplitCasePurchase,
    splitCasePurchase,
  }

  if (Array.isArray(subItems)) {
    formatted.subItems = subItems.map(({ id: _id, ...si }) => ({
      ...si,
      required: si.required ? 1 : 0,
      list: (si.list || []).map((opt) => ({
        name: opt.name,
        price: Number(opt.price),
      })),
    }))
  }

  return formatted
}

const ItemForm = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const isUpdateMode = location.pathname.includes('/update')

  const { addToast } = useToasts()
  const { infoGroupSelected } = useContext(SelectIdGroupContext)
  const { selectedProduct } = useContext(SelectProductContext)
  const { listEventsOpeningByHost } = useSelector((state) => state.event)

  const groupId = infoGroupSelected?.groupId

  useEffect(() => {
    if (groupId) {
      dispatch(getListEventsOpeningByHost(groupId))
    }
  }, [dispatch, groupId])

  const defaultValues = useMemo(() => ({ products: [{ ...DEFAULT_PRODUCT }] }), [])

  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues,
    resolver: yupResolver(validationSchema),
    mode: 'all',
  })

  useEffect(() => {
    if (isUpdateMode && !selectedProduct) {
      history.push('/products-items')
      return
    }

    const productData = selectedProduct
      ? JSON.parse(JSON.stringify(selectedProduct))
      : JSON.parse(JSON.stringify(DEFAULT_PRODUCT))

    const params = new URLSearchParams(location.search)
    if (params.get('action') === 'add-sub-items' && selectedProduct) {
      productData.subItems = [
        ...(productData.subItems || []),
        JSON.parse(JSON.stringify(DEFAULT_SUB_ITEM)),
      ]
    }

    reset({
      selectedEventId: selectedProduct?.eventId || '',
      products: [productData],
    })
  }, [selectedProduct, isUpdateMode, location.search, reset, history])

  const options = useMemo(
    () =>
      (listEventsOpeningByHost || []).map((item) => ({
        value: item.id,
        label: item.title,
      })),
    [listEventsOpeningByHost]
  )

  const onSubmit = useCallback(
    async (_data) => {
      if (isUpdateMode) {
        try {
          const { id, eventId } = selectedProduct
          const data = formatProductData(_data.products[0])

          const { msgResp } = await dispatch(editProductAction(id, eventId, data))
          addToast(msgResp || 'Product updated successfully', {
            appearance: 'success',
            autoDismiss: true,
          })
          history.push('/products-items')
        } catch (error) {
          addToast(error?.msgResp || 'Update failed', { appearance: 'error', autoDismiss: true })
        }
      } else {
        try {
          const { selectedEventId } = _data
          let successCount = 0

          for (const productInput of _data.products) {
            const data = formatProductData(productInput)
            try {
              await dispatch(addProductToEventAction(selectedEventId, data))
              successCount += 1
            } catch (err) {
              addToast(
                `Failed to add product "${productInput.name}": ${err?.msgResp || err.message}`,
                {
                  appearance: 'error',
                  autoDismiss: true,
                }
              )
              break
            }
          }

          if (successCount > 0) {
            addToast(`Successfully added ${successCount} product(s)`, {
              appearance: 'success',
              autoDismiss: true,
            })
            if (successCount === _data.products.length) {
              history.push('/products-items')
            }
          }
        } catch (error) {
          addToast(error?.msgResp || 'Creation failed', { appearance: 'error', autoDismiss: true })
        }
      }
    },
    [isUpdateMode, selectedProduct, dispatch, addToast, history]
  )

  const onCancel = useCallback(() => {
    reset({ products: [{ ...DEFAULT_PRODUCT }] })
    history.push('/products-items')
  }, [reset, history])

  return (
    <form className='desktop-item-form' onSubmit={handleSubmit(onSubmit)}>
      {!selectedProduct && (
        <div>
          {/* Header */}
          <div className='desktop-item-form__header'>
            <h4 className='desktop-item-form__title'>Add Product / Item</h4>
          </div>
          {/* Select Event */}
          <div className='desktop-item-form__event-select'>
            <Controller
              name='selectedEventId'
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  options={options}
                  placeholder='Select Event'
                  styles={customStyles}
                  value={options.find((opt) => opt.value === field.value)}
                  onChange={(val) => field.onChange(val.value)}
                />
              )}
            />
            {errors.selectedEventId && (
              <span className='text-danger fs-xs ml-2'>{errors.selectedEventId.message}</span>
            )}
          </div>
        </div>
      )}
      {/* Product list */}
      <div className='desktop-item-form__body'>
        <DesktopProductList {...{ control, register, setValue, errors, watch, selectedProduct }} />
      </div>
      {/* Footer */}
      <div className='desktop-item-form__footer'>
        <button
          type='button'
          className='btn desktop-item-form__btn desktop-item-form__btn--cancel'
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          type='submit'
          className='btn desktop-item-form__btn desktop-item-form__btn--submit'
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Submitting...' : 'submit'}
        </button>
      </div>
    </form>
  )
}

export default ItemForm
